// src/hooks/useZenoh.ts
// Сессия Zenoh браузера (zenoh-ts) через /zenoh-ws. Единственный живой слой связи
// с роботом. Состояния: idle (не подключались / отключились вручную), connecting,
// connected, reconnecting (связь пропала или не открылась — повтор каждые 5 с).
import { useState, useEffect, useRef, useCallback } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';
import { ZenohHook, ZenohSample, ZenohState } from '../types/zenoh';

// Сколько последних сообщений держать для «Диагностики»
const MAX_MESSAGES = 200;
const RETRY_MS = 5000;
const ALIVE_CHECK_MS = 2000;

export const useZenoh = (): ZenohHook => {
  const [state, setState] = useState<ZenohState>('idle');
  const [session, setSession] = useState<zenoh.Session | null>(null);
  const [messages, setMessages] = useState<ZenohSample[]>([]);
  const sessionRef = useRef<zenoh.Session | null>(null);
  const subscribersRef = useRef<Map<string, zenoh.Subscriber>>(new Map());
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wantedRef = useRef<{ path: string; statusTopic?: string } | null>(null);
  const openingRef = useRef(false);

  const log = useCallback((key: string, value: string) => {
    setMessages(prev => [...prev.slice(-(MAX_MESSAGES - 1)), { key, value, timestamp: Date.now() }]);
  }, []);

  const dropSession = useCallback(() => {
    subscribersRef.current.forEach(sub => sub.undeclare().catch(() => {}));
    subscribersRef.current.clear();
    const s = sessionRef.current;
    sessionRef.current = null;
    setSession(null);
    if (s && !s.isClosed()) s.close().catch(() => {});
  }, []);

  const open = useCallback(async () => {
    const wanted = wantedRef.current;
    if (!wanted || sessionRef.current || openingRef.current) return;
    openingRef.current = true;
    try {
      const s = await zenoh.Session.open(new zenoh.Config(wanted.path));
      openingRef.current = false;
      const current = wantedRef.current;
      if (!current) {
        // Пока открывали, отключились (вручную или ушли со страницы)
        s.close().catch(() => {});
        return;
      }
      sessionRef.current = s;
      setSession(s);
      setState('connected');
      log('system', 'Connected to Zenoh');
      if (current.statusTopic) {
        try {
          const sub = await s.declareSubscriber(current.statusTopic, {
            handler: (sample: zenoh.Sample) => {
              setMessages(prev => [...prev.slice(-(MAX_MESSAGES - 1)), {
                key: sample.keyexpr().toString(),
                value: sample.payload().toString(),
                encoding: sample.encoding().toString(),
                timestamp: Date.now(),
              }]);
            },
          });
          subscribersRef.current.set(current.statusTopic, sub);
          log('system', `Subscribed to status topic ${current.statusTopic}`);
        } catch (err) {
          log('error', `Failed to subscribe to ${current.statusTopic}: ${err instanceof Error ? err.message : String(err)}`);
        }
      }
    } catch (err) {
      openingRef.current = false;
      log('error', `Connection failed: ${err instanceof Error ? err.message : String(err)}`);
      if (wantedRef.current) {
        setState('reconnecting');
        if (!retryRef.current) {
          retryRef.current = setTimeout(() => {
            retryRef.current = null;
            open();
          }, RETRY_MS);
        }
      }
    }
  }, [log]);

  const connect = useCallback(async (configPath: string = '/zenoh-ws', statusTopic?: string) => {
    // Открытие уже идёт — оно увидит новое желание (StrictMode монтирует эффекты дважды)
    wantedRef.current = { path: configPath, statusTopic };
    if (sessionRef.current || openingRef.current) return;
    setState('connecting');
    await open();
  }, [open]);

  const disconnect = useCallback(() => {
    wantedRef.current = null;
    if (retryRef.current) {
      clearTimeout(retryRef.current);
      retryRef.current = null;
    }
    dropSession();
    setState('idle');
    log('system', 'Disconnected from Zenoh');
  }, [dropSession, log]);

  // Сессия закрылась сама (роутер перезапустился, сеть пропала) — переподключаемся
  useEffect(() => {
    const t = setInterval(() => {
      const s = sessionRef.current;
      if (s && s.isClosed() && wantedRef.current) {
        log('error', 'Zenoh session closed, reconnecting');
        dropSession();
        setState('reconnecting');
        open();
      }
    }, ALIVE_CHECK_MS);
    return () => clearInterval(t);
  }, [dropSession, log, open]);

  const put = useCallback(async (key: string, value: string) => {
    const s = sessionRef.current;
    if (!s) {
      log('error', 'Cannot send data: not connected');
      return;
    }
    try {
      await s.put(key, value);
      log(key, value);
    } catch (err) {
      log('error', `Failed to send: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [log]);

  const get = useCallback(async (_key: string): Promise<ZenohSample[]> => [], []);

  const subscribe = useCallback(async (key: string, callback: (sample: ZenohSample) => void) => {
    const s = sessionRef.current;
    if (!s || subscribersRef.current.has(key)) return;
    try {
      const sub = await s.declareSubscriber(key, {
        handler: (sample: zenoh.Sample) => callback({
          key: sample.keyexpr().toString(),
          value: sample.payload().toString(),
          timestamp: Date.now(),
        }),
      });
      subscribersRef.current.set(key, sub);
    } catch (err) {
      log('error', `Failed to subscribe to ${key}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [log]);

  const unsubscribe = useCallback((key: string) => {
    const sub = subscribersRef.current.get(key);
    if (!sub) return;
    sub.undeclare().catch(() => {});
    subscribersRef.current.delete(key);
  }, []);

  // Уход со страницы — закрываем сессию
  useEffect(() => () => {
    wantedRef.current = null;
    if (retryRef.current) clearTimeout(retryRef.current);
    dropSession();
  }, [dropSession]);

  return {
    connect,
    disconnect,
    put,
    get,
    subscribe,
    unsubscribe,
    isConnected: state === 'connected' && session !== null,
    state,
    messages,
    session,
  };
};
